import { useEffect, useState, useCallback } from "react";
import { Bell, Plus, CheckCircle2, AlertTriangle, Info, Megaphone, Users } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const LEVEL = { danger: { i: AlertTriangle, c: "text-rose-600 bg-rose-50" }, warning: { i: AlertTriangle, c: "text-amber-600 bg-amber-50" }, info: { i: Info, c: "text-blue-600 bg-blue-50" } };

function StaffView() {
  const [feed, setFeed] = useState([]);
  const [notices, setNotices] = useState([]);
  const load = useCallback(() => {
    api.get("/notifications/feed").then(({ data }) => setFeed(data));
    api.get("/staff-notifications").then(({ data }) => setNotices(data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const confirm = async (id) => { await api.post(`/staff-notifications/${id}/confirm`); toast.success("確認しました"); load(); };

  return (
    <div>
      <PageHeader title="通知" subtitle="お知らせと職員周知の確認" />
      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <h3 className="font-semibold text-slate-800 mb-3">お知らせ</h3>
          <div className="space-y-2">
            {feed.length ? feed.map((f, i) => {
              const L = LEVEL[f.level] || LEVEL.info;
              return (
                <div key={i} className="flex items-start gap-3 bg-white rounded-xl border border-slate-200 p-3">
                  <span className={`p-1.5 rounded-lg ${L.c}`}><L.i className="w-4 h-4" /></span>
                  <div><div className="text-sm text-slate-700">{f.message}</div><div className="text-xs text-slate-400 mt-0.5">{f.type}・{f.date}</div></div>
                </div>
              );
            }) : <EmptyState title="お知らせはありません" />}
          </div>
        </div>
        <div>
          <h3 className="font-semibold text-slate-800 mb-3">職員周知</h3>
          <div className="space-y-2">
            {notices.length ? notices.map((n) => (
              <div key={n.id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`notice-${n.id}`}>
                <div className="font-medium text-slate-800">{n.title}</div>
                <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{n.body}</p>
                <div className="mt-3">
                  {n.confirmed
                    ? <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> 確認済み（{(n.confirmed_at || "").slice(0, 10)}）</span>
                    : <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => confirm(n.id)} data-testid={`confirm-${n.id}`}>確認しました</Button>}
                </div>
              </div>
            )) : <EmptyState title="周知はありません" />}
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminView() {
  const [notices, setNotices] = useState(null);
  const [offices, setOffices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", office_id: "" });

  const load = useCallback(() => { api.get("/staff-notifications").then(({ data }) => setNotices(data)); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/offices").then(({ data }) => { setOffices(data); if (data.length) setForm((f) => ({ ...f, office_id: data[0].id })); }); }, []);

  const create = async () => {
    if (!form.title || !form.body) { toast.error("件名と本文は必須です"); return; }
    try { await api.post("/staff-notifications", { ...form, all_office_staff: true }); toast.success("職員へ周知しました"); setOpen(false); setForm({ ...form, title: "", body: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!notices) return <Loading />;

  return (
    <div>
      <PageHeader title="通知・職員周知" subtitle="職員への周知と確認状況の管理"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-notice-btn"><Plus className="w-4 h-4 mr-1" /> 周知を作成</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>職員周知を作成</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>事業所</Label>
                  <Select value={form.office_id} onValueChange={(v) => setForm({ ...form, office_id: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label>件名</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="notice-title-input" /></div>
                <div><Label>本文</Label><Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="mt-1" data-testid="notice-body-input" /></div>
              </div>
              <DialogFooter><Button onClick={create} className="bg-blue-600 hover:bg-blue-700" data-testid="save-notice-btn"><Megaphone className="w-4 h-4 mr-1" /> 周知する</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="space-y-3">
        {notices.length ? notices.map((n) => (
          <div key={n.id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`admin-notice-${n.id}`}>
            <div className="flex items-start justify-between">
              <div><div className="font-medium text-slate-800">{n.title}</div><p className="text-sm text-slate-600 mt-1">{n.body}</p></div>
              <span className="text-xs text-slate-500 flex items-center gap-1 shrink-0 ml-3"><Users className="w-3.5 h-3.5" /> {n.confirmed_count}/{n.target_count} 確認</span>
            </div>
          </div>
        )) : <EmptyState title="周知はありません" />}
      </div>
    </div>
  );
}

export default function Notifications() {
  const { user } = useAuth();
  return user.role === "staff" ? <StaffView /> : <AdminView />;
}
